import { ArrowRight, ShieldCheck, Key, Wrench, Scale, MapPin, ArrowUpRight, Loader, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { useProperties } from '../hooks/useProperties';
import LazyImage from '../components/LazyImage';
import Testimonials from '../components/Testimonials';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { data: queryData, isLoading } = useProperties(1, {});

  const heroImages = [
    "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&h=1200&q=75",
    "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1600&h=1200&q=75",
    "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&h=1200&q=75"
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  return (
    <div className="">
      <Helmet>
        <title>ProjectProse | UK Property Management &amp; Guaranteed Rent for Landlords</title>
        <meta
          name="description"
          content="ProjectProse manages 150+ properties UK-wide, offering landlords guaranteed rent, vetted tenants and full compliance, plus a curated portfolio of vacation and short-term rentals for guests."
        />
        <link rel="canonical" href="https://www.project-prose.com/" />
      </Helmet>
      <section className="relative min-h-screen bg-white border-b border-gray-200">
        <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 gap-0">
          <div className="flex items-center justify-center px-6 md:px-12 lg:px-20 py-20 pt-32">
            <div className="space-y-6 z-10 w-full md:max-w-2xl">
              <span className="text-prose-brand font-semibold tracking-widest uppercase text-sm italic" data-aos="fade-in">
                Property Management & Rental Services
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display leading-tight text-prose-black" data-aos="fade-up">
                Welcome to <span className="text-prose-brand">ProjectProse</span>
              </h1>
              <p className="text-gray-600 text-sm sm:text-base md:text-lg w-full md:max-w-md text-justify" data-aos="fade-up" data-aos-delay="100">
                ProjectProse is a forward-thinking property company offering both professional management services for landlords and a premium portfolio of short-term vacation rentals and serviced accommodation. Based in the UK, we specialise in handling property complexities while providing quality homes for guests seeking flexible, memorable stays.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto" data-aos="fade-up" data-aos-delay="200">
                <Link to="/contact" className="w-full sm:w-auto bg-prose-brand text-white px-8 py-4 flex items-center justify-center gap-2 hover:bg-prose-brand/90 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 rounded-lg font-semibold">
                  Get Started <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/services" className="w-full sm:w-auto border-2 border-prose-brand text-prose-brand px-8 py-4 text-center hover:bg-prose-brand/5 hover:-translate-y-1 hover:shadow-md transition-all duration-300 rounded-lg font-semibold">
                  Learn More
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-6 pt-8 mt-8 border-t border-gray-200" data-aos="fade-up" data-aos-delay="300">
                <div>
                  <p className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-prose-brand">150+</p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Properties Managed</p>
                </div>
                <div>
                  <p className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-prose-brand">95%</p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Rental Recovery</p>
                </div>
                <div>
                  <p className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-prose-brand">UK Wide</p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Nationwide Coverage</p>
                </div>
                <div>
                  <p className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-prose-brand">24/7</p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Support Available</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative h-64 sm:h-80 md:h-full overflow-hidden flex flex-col bg-gray-100">
            {/* Preload current and next image only */}
            {heroImages.map((image, index) => {
              const shouldRender = index === currentSlide || index === (currentSlide + 1) % heroImages.length;
              return shouldRender ? (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{
                    backgroundImage: `url(${image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
              ) : null;
            })}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-prose-brand font-semibold tracking-widest text-sm uppercase italic" data-aos="fade-in">Who We Are</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-bold text-prose-black mt-2 mb-6" data-aos="fade-up">Your Trusted Partner</h2>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed mb-6 text-justify" data-aos="fade-up" data-aos-delay="100">
              ProjectProse Property Management is a forward-thinking, professional property management company committed to providing landlords with a seamless experience. Based in the UK, we specialise in handling the complexities of property ownership, ensuring landlords maximise their investments while tenants enjoy high-quality living spaces.
            </p>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed mb-8 text-justify" data-aos="fade-up" data-aos-delay="200">
              We offer comprehensive solutions tailored to your needs - from guaranteed rental income to professional tenant management and expert legal compliance.
            </p>
            <Link 
              to="/about"
              className="inline-flex items-center gap-2 bg-prose-brand text-white px-8 py-4 rounded-lg font-semibold hover:bg-prose-brand/90 hover:scale-105 hover:shadow-lg transition-all duration-300"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              Read Our Full Story <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="space-y-8" data-aos="fade-up">
            {[
              { number: "150+", label: "Successful Lettings" },
              { number: "50M+", label: "Portfolio Value Managed" },
              { number: "99%", label: "Lease Compliance Rate" },
              { number: "300+", label: "Happy Landlords Served" }
            ].map((item, i) => (
              <div key={i} className="flex gap-4 pb-8 border-b border-gray-200 last:border-b-0">
                <div className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-prose-gold">{item.number}</div>
                <div className="flex flex-col justify-center">
                  <p className="text-gray-600 text-xs sm:text-sm uppercase tracking-widest font-semibold">{item.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-prose-brand text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-prose-gold font-semibold tracking-widest text-sm uppercase italic" data-aos="fade-in">What We Do</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-bold mt-2 mb-4" data-aos="fade-up">Our Services</h2>
            <p className="text-white/80 text-sm sm:text-base md:text-lg max-w-2xl mx-auto" data-aos="fade-up" data-aos-delay="200">Property management for landlords and premium vacation rentals for guests</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { icon: <ShieldCheck size={32} />, title: "Guaranteed Rent", desc: "Fixed monthly income regardless of occupancy" },
              { icon: <Key size={32} />, title: "Tenant Sourcing", desc: "Vetted, quality tenants placed quickly" },
              { icon: <Wrench size={32} />, title: "Maintenance", desc: "24/7 emergency support and repairs" },
              { icon: <Scale size={32} />, title: "Compliance", desc: "Full legal and safety compliance" }
            ].map((service, i) => (
              <div 
                key={i} 
                className="group p-8 rounded-xl border border-white/20 hover:border-prose-gold hover:bg-white/10 transition-all duration-300 cursor-pointer"
                data-aos="fade-up"
                data-aos-delay={`${i * 100}`}
              >
                <div className="text-prose-gold mb-4 group-hover:scale-110 transition-transform duration-300">
                  {service.icon}
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold mb-3 group-hover:text-prose-gold transition-colors">{service.title}</h3>
                <p className="text-white/70 text-xs sm:text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link 
              to="/services"
              data-aos="fade-up"
              data-aos-delay="100"
              className="inline-flex items-center gap-2 bg-prose-gold text-prose-brand px-10 py-4 rounded-lg font-semibold hover:bg-white transition-all"
            >
              Explore All Services <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>


      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-4">For Landlords</h2>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg max-w-2xl mx-auto">Simple, transparent process to get your properties managed</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: 1, title: "Assessment", desc: "We evaluate your property and discuss your needs" },
              { step: 2, title: "Agreement", desc: "Choose your service package and sign the contract" },
              { step: 3, title: "Activation", desc: "We prepare and market your property professionally" },
              { step: 4, title: "Management", desc: "Monthly support with guaranteed returns" }
            ].map((item, i) => (
              <div key={i} className="relative" data-aos="fade-up" data-aos-delay={`${i * 100}`}>
                <div className="flex flex-col items-center">
                  <div className={`w-14 sm:w-16 h-14 sm:h-16 rounded-full text-white flex items-center justify-center font-display text-lg sm:text-xl md:text-2xl font-bold mb-4 transition-all ${
                    i % 2 === 1 
                      ? 'bg-prose-gold shadow-[0_0_20px_rgba(212,175,55,0.3)]' 
                      : 'bg-prose-brand'
                  }`}>
                    {item.step}
                  </div>
                  <h3 className="font-bold text-base sm:text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-xs sm:text-sm text-center">{item.desc}</p>
                </div>
                {i < 3 && (
                  <div className={`hidden md:block absolute top-8 left-full w-8 h-0.5 ${
                    i % 2 === 1 ? 'bg-prose-gold/50' : 'bg-prose-brand/30'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-prose-brand text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-4">For Guests</h2>
            <p className="text-white/80 text-sm sm:text-base md:text-lg max-w-2xl mx-auto">Book your perfect vacation or short-term stay in minutes</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: 1, title: "Browse", desc: "Explore our portfolio of quality properties for your dates" },
              { step: 2, title: "Select", desc: "Choose the perfect accommodation for your needs" },
              { step: 3, title: "Book", desc: "Secure your booking with transparent, upfront pricing" },
              { step: 4, title: "Enjoy", desc: "Check in to your quality home with dedicated guest support" }
            ].map((item, i) => (
              <div key={i} className="relative" data-aos="fade-up" data-aos-delay={`${i * 100}`}>
                <div className="flex flex-col items-center">
                  <div className={`w-14 sm:w-16 h-14 sm:h-16 rounded-full text-white flex items-center justify-center font-display text-lg sm:text-xl md:text-2xl font-bold mb-4 transition-all ${
                    i % 2 === 1 
                      ? 'bg-prose-gold' 
                      : 'bg-white/20'
                  }`}>
                    {item.step}
                  </div>
                  <h3 className="font-bold text-base sm:text-lg mb-2 text-white">{item.title}</h3>
                  <p className="text-white/70 text-xs sm:text-sm text-center">{item.desc}</p>
                </div>
                {i < 3 && (
                  <div className={`hidden md:block absolute top-8 left-full w-8 h-0.5 ${
                    i % 2 === 1 ? 'bg-prose-gold/50' : 'bg-white/20'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-4">Featured Properties</h2>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg max-w-2xl mx-auto">Explore our premium vacation rental properties</p>
          </div>
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader size={40} className="text-prose-brand animate-spin" />
            </div>
          ) : queryData?.properties && queryData.properties.length > 0 ? (
            <>
              <div className="flex lg:grid lg:grid-cols-3 gap-6 mb-12 overflow-x-auto pb-4 lg:overflow-x-visible snap-x snap-mandatory scrollbar-gold">
                {queryData?.properties?.slice(0, 3).map((property) => (
                  <Link 
                    key={property.id} 
                    to={`/property/${property.id}`} 
                    className="bg-white group cursor-pointer overflow-hidden border border-gray-200 rounded-lg flex-shrink-0 w-full sm:w-96 lg:w-auto lg:flex-shrink snap-center hover:shadow hover:scale-105 transition-all duration-300 transform"
                  >
                    <div className="relative h-72 overflow-hidden">
                      <LazyImage
                        src={property.images?.[0]?.url || 'https://via.placeholder.com/800x600.png?text=No+Image'}
                        alt={property.title}
                        className="w-full h-full group-hover:scale-110"
                        onLoad={() => {}}
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-prose-brand/90 text-white text-[10px] uppercase tracking-widest px-3 py-1 backdrop-blur-sm">
                          {property.status}
                        </span>
                      </div>
                    </div>

                    <div className="p-8 space-y-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-xl font-bold text-prose-black group-hover:text-prose-brand transition-colors">
                            {property.title}
                          </h3>
                          <p className="flex items-center gap-1 text-gray-400 text-sm mt-1">
                            <MapPin size={14} /> {property.address.split('\n')[0]}
                          </p>
                        </div>
                        <div className="p-2 border border-gray-100 rounded-full group-hover:bg-prose-gold group-hover:border-prose-gold group-hover:text-prose-black group-hover:rotate-45 transition-all duration-300">
                          <ArrowUpRight size={20} />
                        </div>
                      </div>

                      <div className="pt-6 border-t border-gray-50 grid grid-cols-3 gap-4">
                        <div className="flex flex-col items-start gap-1">
                          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Bedrooms</span>
                          <span className="text-sm sm:text-lg font-bold text-prose-brand">{property.bedrooms}</span>
                        </div>
                        <div className="flex flex-col items-start gap-1">
                          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Bathrooms</span>
                          <span className="text-sm sm:text-lg font-bold text-prose-brand">{property.bathrooms}</span>
                        </div>
                        <div className="flex flex-col items-start gap-1">
                          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Type</span>
                          <span className="text-sm sm:text-lg font-bold text-prose-brand capitalize">{property.type}</span>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-50">
                        <p className="text-lg sm:text-xl md:text-2xl font-display font-bold text-prose-gold">£{parseInt(property.price).toLocaleString()}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 px-6">
              <div className="mb-4 p-4 bg-gray-100 rounded-full">
                <Building2 size={48} className="text-gray-400" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-prose-black mb-2">No Featured Properties Yet</h3>
              <p className="text-gray-500 text-sm sm:text-base mb-8 max-w-md text-center">
                We're currently updating our featured properties collection. Check back soon for amazing options!
              </p>
            </div>
          )}

          <div className="text-center">
            <Link 
              to="/properties"
              className="inline-block bg-prose-brand text-white px-10 py-4 rounded-lg font-semibold hover:bg-prose-brand/90 transition-all"
            >
              View All Properties
            </Link>
          </div>
        </div>
      </section>

      <Testimonials />

      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="p-12 bg-gradient-to-r from-prose-black via-prose-brand to-prose-black text-white flex flex-col md:flex-row items-center justify-between gap-8 rounded-2xl relative overflow-hidden" data-aos="fade-up">
            <div className="absolute inset-0 opacity-10 pointer-events-none"><div className="absolute top-0 right-0 w-96 h-96 bg-prose-gold rounded-full filter blur-3xl"></div></div>
            <div className="relative z-10">
              <h3 className="text-2xl font-display font-bold mb-2">Ready to maximize your portfolio?</h3>
              <p className="text-gray-200">Take the first step toward stress-free property management and guaranteed returns.</p>
            </div>
            <Link 
              to="/contact"
              className="relative z-10 whitespace-nowrap bg-prose-gold text-prose-brand px-8 py-4 rounded-lg text-sm font-bold tracking-widest hover:bg-white hover:text-prose-brand transition-all hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]"
            >
              Get Started Today
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;